<template>
  <!-- Teleported to <body> and position:fixed, so the popover never takes part
       in the list's layout — no row can shift or resize because of it. -->
  <teleport to="body">
    <div
      ref="card"
      class="vp-preview"
      :class="[`vp-${placement}`, { 'is-ready': ready, 'is-clamped': clamped }]"
      :style="style"
      aria-hidden="true"
    >
      <div class="vp-hero" v-if="showHero">
        <img :src="heroImage" alt="" class="vp-hero-img" @error="onHeroError" />
        <span v-if="extraPhotoCount" class="vp-photo-count">
          +{{ extraPhotoCount }} photo{{ extraPhotoCount === 1 ? "" : "s" }}
        </span>
      </div>

      <div class="vp-body">
        <p v-if="visible('title')" class="vp-title">{{ title }}</p>

        <div class="vp-badges">
          <span v-if="visible('serviceType2') && serviceType2" class="vp-badge">{{ serviceType2 }}</span>
          <span v-if="visible('serviceType1') && serviceType1" class="vp-badge vp-badge--soft">{{ serviceType1 }}</span>
        </div>

        <div class="vp-meta">
          <span v-if="visible('location') && locationText" class="vp-meta-item">
            <img src="../icons/Frame 38 (4).svg" alt="" class="vp-meta-icon" />
            <span class="vp-meta-text">{{ locationText }}</span>
          </span>
          <span v-if="postedAt" class="vp-meta-item">
            <img src="../icons/Frame 38 (5).svg" alt="" class="vp-meta-icon" />
            <span class="vp-meta-text">{{ postedAt }}</span>
          </span>
          <span v-if="viewCount !== null" class="vp-meta-item">
            <img src="../icons/Frame 38 (2).svg" alt="" class="vp-meta-icon" />
            <span class="vp-meta-text">{{ viewCount }} view{{ viewCount === 1 ? "" : "s" }}</span>
          </span>
        </div>

        <p v-if="visible('description') && compactDescription" class="vp-desc">{{ compactDescription }}</p>

        <p class="vp-hint">Click to open the full post</p>
      </div>
    </div>
  </teleport>
</template>

<script>
import { getPreviewDetail } from "@/services/viewPostPreviewService";
import {
  loadServiceTypesMap,
  buildFieldVisibility,
  isFieldVisible,
} from "@/utils/fieldVisibility";

// Keep the card clear of the viewport edges and off the row it describes.
const GAP = 12;
const MARGIN = 12;
// Below this much vertical room the hero image is dropped to buy back 132px
// for the text. Roughly the height of the card without its photo.
const COMPACT_BELOW = 330;
// A card shorter than this tells you nothing, so on a very short window we
// stop shrinking and accept a little overlap with the row.
const MIN_HEIGHT = 180;

const clamp = (value, low, high) => Math.min(Math.max(value, low), high);

export default {
  name: "ViewPostPreview",
  props: {
    // A list row as the dashboard shapes it (see services/dashboardService.js):
    // servicesID, servicesName, Description, location, serviceType1/2,
    // createdTime, images[] and an already-resolved imageUrl.
    service: {
      type: Object,
      required: true,
    },
    // Viewport rect of the hovered row plus the cursor's x, measured by the
    // parent at the moment the dwell completed.
    anchor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      // Detail-page payload — only fills in the view count; everything else
      // renders immediately from `service`, so the card never waits on it.
      detail: null,
      fieldVisibility: {},
      heroFailed: false,
      ready: false,
      placement: "bottom",
      // Hero dropped because the card is short on vertical room.
      compact: false,
      // Content taller than the room available — the body fades out at the cut.
      clamped: false,
      style: { top: "-9999px", left: "-9999px" },
    };
  },
  computed: {
    title() {
      return this.service.servicesName || "Untitled Service";
    },
    serviceType1() {
      return this.detail?.serviceType1 || this.service.serviceType1 || "";
    },
    serviceType2() {
      return this.detail?.serviceType2 || this.service.serviceType2 || "";
    },
    locationText() {
      const s = this.service;
      if (s.city && s.country) return `${s.city}, ${s.country}`;
      return s.location || this.detail?.location || "";
    },
    postedAt() {
      const raw = this.service.createdTime || this.detail?.createdTime;
      if (!raw) return "";
      const date = new Date(raw);
      if (isNaN(date.getTime())) return "";
      // Same shape /view-post prints next to the listing title.
      return date.toLocaleString([], {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },
    viewCount() {
      // null until the detail lookup lands — the chip stays out rather than
      // flashing a wrong zero.
      const count = this.detail?.viewCount;
      return typeof count === "number" ? count : null;
    },
    activeImages() {
      return (this.service.images || []).filter(
        (img) => img.isActive === true || img.isActive === "true"
      );
    },
    extraPhotoCount() {
      return Math.max(0, this.activeImages.length - 1);
    },
    heroImage() {
      // Already resolved (real photo or category placeholder) when the list
      // loaded — reusing it means the preview costs no image request.
      return this.service.imageUrl || "";
    },
    showHero() {
      // In compact mode the 132px hero is the first thing to go — see
      // position(). Losing the photo beats clipping the text mid-sentence.
      return (
        !this.compact &&
        this.visible("images") &&
        !!this.heroImage &&
        !this.heroFailed
      );
    },
    /**
     * A readable excerpt of the same Description /view-post renders.
     *
     * The detail page shows the Description verbatim, keeping the author's own
     * line breaks. At preview size that structure doesn't survive the clamp, so
     * here it's normalised to clean lines — tabs and exotic spaces collapsed,
     * blank lines dropped — and truncated. CSS clamps what's left to six lines.
     */
    compactDescription() {
      const raw = this.detail?.Description ?? this.service.Description ?? "";
      if (!raw) return "";

      const lines = String(raw)
        .replace(/\r\n/g, "\n")
        .split("\n")
        .map((line) =>
          line
            .replace(/[\t\u00A0\u2000-\u200B\u202F\u3000\uFEFF]/g, " ")
            .replace(/ {2,}/g, " ")
            .trim()
        )
        .filter(Boolean)
        .slice(0, 8);

      const text = lines.join("\n");
      return text.length > 600 ? `${text.slice(0, 600).trimEnd()}…` : text;
    },
  },
  async mounted() {
    // Place it from the row data we already have, so the card is on screen in
    // the same frame it mounts — the lookups below only refine it.
    this.$nextTick(this.position);

    const [map, detail] = await Promise.all([
      loadServiceTypesMap(),
      getPreviewDetail(this.service.servicesID),
    ]);

    // A fast mouse-out can unmount us mid-flight.
    if (this._destroyed) return;

    this.fieldVisibility = buildFieldVisibility(
      map,
      detail?.serviceType1 || this.service.serviceType1,
      detail?.serviceType2 || this.service.serviceType2
    );
    this.detail = detail;

    // Fields may have appeared or dropped out — re-measure before settling.
    this.$nextTick(this.position);
  },
  beforeUnmount() {
    this._destroyed = true;
  },
  methods: {
    visible(field) {
      return isFieldVisible(this.fieldVisibility, field);
    },
    onHeroError() {
      this.heroFailed = true;
      this.$nextTick(this.position);
    },
    /**
     * Place the card above or below the row, centred on the cursor.
     *
     * Rows run the full width of the list, so there is never a meaningful
     * "beside the row" — a card out at the row's right edge lands a screen's
     * width away from the pointer on a wide monitor. Only the vertical axis
     * has a real choice to make, and it's made on which side has more room
     * rather than on which side happens to fit first: picking bottom whenever
     * it fits, then falling back to a top that doesn't, is what let the old
     * clamp drop the card on top of the row it was previewing.
     */
    position() {
      const el = this.$refs.card;
      if (!el) return;

      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const a = this.anchor;

      // Vertical room on each side of the row, once the gap to the row and the
      // viewport margin are paid for.
      const roomBelow = vh - MARGIN - (a.bottom + GAP);
      const roomAbove = a.top - GAP - MARGIN;

      // Below on a tie: a card hanging off the row reads as belonging to it.
      const below = roomBelow >= roomAbove;
      const room = Math.max(below ? roomBelow : roomAbove, MIN_HEIGHT);

      // Depends only on the room available, never on the card's own height, so
      // toggling it can't oscillate.
      const compact = room < COMPACT_BELOW;
      if (compact !== this.compact) {
        this.compact = compact;
        this.$nextTick(this.position);
        return;
      }

      // Measure unconstrained, so a card that shrank for a cramped row can
      // grow back for a roomy one.
      el.style.maxHeight = "none";
      const natural = el.offsetHeight;
      const w = el.offsetWidth;
      const h = Math.min(natural, room);

      const top = below ? a.bottom + GAP : a.top - GAP - h;
      // fallbackX is the listing's content block, not the row's midpoint —
      // a 1216px row centres nowhere near what the cursor was resting on.
      const left = (a.cursorX ?? a.fallbackX ?? a.left + a.width / 2) - w / 2;

      this.placement = below ? "bottom" : "top";
      this.clamped = natural > h;
      this.style = {
        // Never let it hang off the viewport.
        top: `${Math.round(clamp(top, MARGIN, Math.max(MARGIN, vh - h - MARGIN)))}px`,
        left: `${Math.round(clamp(left, MARGIN, Math.max(MARGIN, vw - w - MARGIN)))}px`,
        maxHeight: `${Math.round(h)}px`,
      };
      this.ready = true;
    },
  },
};
</script>

<style scoped>
.vp-preview {
  position: fixed;
  z-index: 4000;
  width: 360px;
  max-width: calc(100vw - 24px);
  box-sizing: border-box;
  background: #ffffff;
  border: 1px solid #e9eaeb;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(10, 13, 18, 0.16);
  overflow: hidden;
  font-family: "Inter", sans-serif;
  /* Read-only card: it must never swallow a click meant for the row beneath,
     and letting the cursor "enter" it would fight the row's mouseleave. */
  pointer-events: none;
  opacity: 0;
  transform: translateY(4px);
  transition: opacity 0.14s ease, transform 0.14s ease;
}

.vp-preview.is-ready {
  opacity: 1;
  transform: translateY(0);
}

/* The card is capped to the room beside the row, so its content can outrun it.
   Fade the cut instead of slicing a line of text in half — the card can't be
   scrolled (pointer-events: none), and the full post is one click away. */
.vp-preview.is-clamped::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 40px;
  background: linear-gradient(rgba(255, 255, 255, 0), #ffffff 88%);
}

.vp-hero {
  position: relative;
  width: 100%;
  height: 132px;
  background: #f3f4f6;
}

.vp-hero-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.vp-photo-count {
  position: absolute;
  right: 8px;
  bottom: 8px;
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(10, 13, 18, 0.72);
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  line-height: 16px;
}

.vp-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px 14px;
}

.vp-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  color: #181d27;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.vp-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

/* Same gradient-outline treatment the list row and detail page use. */
.vp-badge {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 2px 8px;
  border-radius: 4px;
  border: 0.77px solid transparent;
  background: linear-gradient(white, white) padding-box,
    var(--color-primary-gradient) border-box;
  font-size: 11px;
  font-weight: 600;
  line-height: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background-image: var(--color-primary-gradient);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
}

.vp-badge--soft {
  background: #f3f4f6;
  border-color: #e9eaeb;
  color: #6b7280;
  -webkit-text-fill-color: #6b7280;
}

.vp-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
}

.vp-meta-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  max-width: 100%;
}

.vp-meta-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

.vp-meta-text {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.vp-desc {
  margin: 0;
  font-size: 12.5px;
  line-height: 18px;
  color: #4b5563;
  white-space: pre-line;
  display: -webkit-box;
  -webkit-line-clamp: 6;
  line-clamp: 6;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.vp-hint {
  margin: 2px 0 0;
  padding-top: 8px;
  border-top: 1px solid #f0f1f3;
  font-size: 11px;
  font-weight: 500;
  color: #9ca3af;
}

@media (prefers-reduced-motion: reduce) {
  .vp-preview {
    transition: none;
    transform: none;
  }
}
</style>
